import React, { useState, useEffect } from 'react';
import * as Sentry from "@sentry/react";
import { Heart } from 'lucide-react';
import { likePost, unlikePost } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { CommunityError } from "../../errors/sentryErrors";
import { trackEvent } from "../../utils/analytics";

const LikeButton = ({ post, className = "", variant = "default" }) => {
  const { user, isBanned } = useAuth();
  const isOwner = user?.id === post?.user?.id;
  // Own post or banned account: the button is greyed out and does nothing.
  const blocked = isOwner || isBanned;
  
  // Track optimistic state
  const [liked, setLiked] = useState(post?.isLikedByViewer || false);
  const [count, setCount] = useState(post?.counters?.likeCount || 0);
  const [loading, setLoading] = useState(false);

  // Sync state if post prop changes
  useEffect(() => {
    setLiked(post?.isLikedByViewer || false);
    setCount(post?.counters?.likeCount || 0);
  }, [post]);

  const handleToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.error('Please log in to like this post.');
      return;
    }
    if (blocked) {
      return; // Handled by disabled state/tooltip
    }
    if (loading) return;

    // Optimistic Update
    const newLiked = !liked;
    const newCount = newLiked ? count + 1 : Math.max(0, count - 1);
    
    setLiked(newLiked);
    setCount(newCount);
    setLoading(true);

    if (newLiked) {
      toast.success("You liked this trip", { autoClose: 2000, icon: "❤️" });
    }

    try {
      if (newLiked) {
        await likePost(post.id);
      } else {
        await unlikePost(post.id);
      }
      trackEvent(newLiked ? "post_liked" : "post_unliked", { post_id: post.id, variant });
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(error instanceof Error ? error.message : String(error)));
      });
      // Rollback on error
      setLiked(!newLiked);
      setCount(!newLiked ? newCount + 1 : Math.max(0, newCount - 1));
      
      if (error.status === 429) {
        toast.error(`Slow down. Try again in ${error.retryAfter || 'a few'} seconds.`);
      } else {
        toast.error('Failed to update like. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (variant === "hero") {
    return (
      <div className={`relative group ${className}`}>
        <button
          onClick={blocked ? undefined : handleToggle}
          disabled={blocked || loading}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-base transition-all duration-300 shadow-xl border
            ${blocked ? 'opacity-50 cursor-not-allowed bg-white/10 text-white border-white/10' : 
              liked ? 'bg-rose-500 hover:bg-rose-600 text-white border-rose-500 hover:shadow-rose-500/30' : 'bg-black/20 hover:bg-black/40 backdrop-blur-md text-white border-white/20'}
          `}
        >
          <Heart size={18} className={liked ? 'fill-white' : 'text-white'} />
          <span>{count.toLocaleString()}</span>
        </button>
        {isOwner && (
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
            You can't like your own post
          </div>
        )}
      </div>
    );
  }

  if (variant === "large") {
    return (
      <div 
        className={`relative border border-gray-100 rounded-xl p-3 flex flex-col items-center justify-center gap-1.5 hover:border-red-200 hover:bg-red-50 transition-colors cursor-pointer text-center group ${blocked ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
        onClick={blocked ? undefined : handleToggle}
      >
        <div className={`transition-transform duration-300 group-hover:scale-110 ${liked ? 'text-red-500' : 'text-red-400'}`}>
          <Heart size={18} className={liked ? 'fill-red-500' : ''} />
        </div>
        <div>
          <div className="font-bold text-gray-900 text-sm mb-0">{count.toLocaleString()}</div>
          <div className="text-[9px] text-gray-400 uppercase tracking-widest font-bold">Likes</div>
        </div>
        
        {isOwner && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
            You can't like your own post
          </div>
        )}
      </div>
    );
  }

  // "overlay": sits straight on a photo with no background of its own — a white heart and count with a soft shadow so they read on any
  // picture, and the heart fills red once liked.
  const overlay = variant === "overlay";

  return (
    <div className={`relative group flex items-center gap-1 ${className}`}>
      <button
        onClick={handleToggle}
        disabled={blocked}
        className={`p-2 rounded-full transition-all duration-300 flex items-center justify-center
          ${blocked ? 'opacity-50 cursor-not-allowed' : overlay ? 'hover:bg-black/20 active:scale-90' : 'hover:bg-gray-100 active:scale-90'}
        `}
        aria-label="Like post"
      >
        <Heart 
          size={20} 
          className={`transition-colors duration-300 ${
            overlay
              ? liked ? 'fill-red-500 text-red-500 drop-shadow-md' : 'text-white drop-shadow-md'
              : liked ? 'fill-red-500 text-red-500' : 'text-gray-600'
          }`} 
        />
      </button>
      {count > 0 && (
        <span className={`text-sm font-medium ${overlay ? 'text-white [text-shadow:0_1px_3px_rgb(0_0_0/0.6)]' : 'text-gray-600'}`}>{count}</span>
      )}
      
      {isOwner && (
        <div className="absolute top-full right-0 mt-2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">
          You can't like your own post
        </div>
      )}
    </div>
  );
};

export default LikeButton;
